/**
 * LocalStorageManager
 *
 * Handles all localStorage operations for PSP/CRE Tool v2.0
 * Maintains backward compatibility with v1.0 scenarios
 *
 * ENHANCED VERSION - Adds new convenience methods and features
 *
 * CHANGELOG:
 * BUG-073 FIX (March 12, 2026):
 * - Removed 14x console.log statements from saveScenario()
 * - Removed unnecessary JSON.parse(serialized) debug round-trip from saveScenario()
 *   ROOT CAUSE: Extra JSON.parse on every auto-save was doubling serialization cost
 *   on large scenarios (109,920-person corps = significant object graph)
 * - Removed console.log from migrateV1ToV2() (fires on legacy scenario load)
 * - All console.error in catch blocks preserved (legitimate error surfacing)
 */

// BUG-FF-012 / F1-FIX: LZ-string compression for localStorage quota fix
import { lsWrite, lsRead } from '../utils/lsStore.js';

const STORAGE_KEYS = {
  CURRENT_SCENARIO: 'psp_cre_current_scenario_v2',
  SCENARIOS_LIST: 'psp_cre_scenarios_list_v2',
  USER_PREFERENCES: 'psp_cre_user_preferences',
  SCENARIO_HISTORY: 'psp_cre_scenario_history_v2',  // NEW
  V1_SCENARIOS: 'psp_cre_scenarios' // Legacy v1.0 key for migration
};

class LocalStorageManager {
  
  /**
   * Save a scenario to localStorage
   * @param {string} key - Storage key (e.g., 'current' or scenario ID)
   * @param {object} scenario - Scenario data object
   * @returns {boolean} Success status
   */
  static saveScenario(key, scenario) {
    try {
      const storageKey = key === 'current' ? STORAGE_KEYS.CURRENT_SCENARIO : `psp_cre_scenario_${key}`;
      // BUG-FF-012: compress scenario JSON (60–80% reduction)
      const ok = lsWrite(storageKey, scenario);
      if (!ok) {
        console.error('Error saving scenario: storage write failed (quota?)');
        return false;
      }

      // Update scenarios list if not current
      if (key !== 'current') {
        this.updateScenariosList(key, scenario);
      }

      return true;
    } catch (error) {
      console.error('Error saving scenario:', error);
      return false;
    }
  }
  
  /**
   * Load a scenario from localStorage
   * @param {string} key - Storage key (e.g., 'current' or scenario ID)
   * @returns {object|null} Scenario data or null if not found
   */
  static loadScenario(key) {
    try {
      const storageKey = key === 'current' ? STORAGE_KEYS.CURRENT_SCENARIO : `psp_cre_scenario_${key}`;
      // BUG-FF-012: lsRead handles both compressed (z: prefix) and legacy plain JSON
      const scenario = lsRead(storageKey);

      if (!scenario) {
        return null;
      }
      
      // Migrate v1.0 scenarios to v2.0 format if needed
      if (!scenario.version || scenario.version === '1.0') {
        return this.migrateV1ToV2(scenario);
      }
      
      return scenario;
    } catch (error) {
      console.error('Error loading scenario:', error);
      return null;
    }
  }
  
  /**
   * Delete a scenario from localStorage
   * @param {string} key - Storage key
   * @returns {boolean} Success status
   */
  static deleteScenario(key) {
    try {
      const storageKey = key === 'current' ? STORAGE_KEYS.CURRENT_SCENARIO : `psp_cre_scenario_${key}`;
      localStorage.removeItem(storageKey);
      
      // Remove from scenarios list
      if (key !== 'current') {
        this.removeFromScenariosList(key);
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting scenario:', error);
      return false;
    }
  }
  
  // ============================================================================
  // NEW CONVENIENCE METHODS
  // ============================================================================
  
  /**
   * Get current scenario (convenience method)
   * @returns {object|null} Current scenario or null
   */
  static getCurrentScenario() {
    return this.loadScenario('current');
  }
  
  /**
   * Save current scenario (convenience method)
   * @param {object} scenario - Scenario to save as current
   * @returns {boolean} Success status
   */
  static saveCurrentScenario(scenario) {
    return this.saveScenario('current', scenario);
  }
  
  // ============================================================================
  // SCENARIOS LIST
  // ============================================================================
  
  /**
   * Get list of all saved scenarios
   * @returns {array} Array of scenario metadata
   */
  static getScenariosList() {
    try {
      // F1-FIX: lsRead handles compressed and legacy plain JSON
      return lsRead(STORAGE_KEYS.SCENARIOS_LIST) || [];
    } catch (error) {
      console.error('Error getting scenarios list:', error);
      return [];
    }
  }
  
  /**
   * Update scenarios list with new/modified scenario
   * @private
   */
  static updateScenariosList(key, scenario) {
    try {
      const list = this.getScenariosList();
      const existing = list.findIndex(item => item.id === key);
      
      const metadata = {
        id: key,
        name: scenario.scenario_name || 'Unnamed Scenario',
        version: scenario.version || '2.0',
        mode: scenario.mode || 'STANDARD',
        force_type: scenario.force?.echelon || scenario.force_type || 'BRIGADE',
        created_at: scenario.created_at || new Date().toISOString(),
        modified_at: scenario.modified_at || new Date().toISOString(),
        // NEW: Enhanced metadata
        operation_type: scenario.operation_parameters?.operation_type,
        duration_days: scenario.operation_parameters?.duration_days
      };
      
      if (existing >= 0) {
        list[existing] = metadata;
      } else {
        list.push(metadata);
      }
      
      // NEW: Sort by modified date (most recent first)
      list.sort((a, b) => new Date(b.modified_at) - new Date(a.modified_at));
      
      // F1-FIX: compress scenarios list
      lsWrite(STORAGE_KEYS.SCENARIOS_LIST, list);
    } catch (error) {
      console.error('Error updating scenarios list:', error);
    }
  }
  
  /**
   * Remove scenario from list
   * @private
   */
  static removeFromScenariosList(key) {
    try {
      const list = this.getScenariosList();
      const filtered = list.filter(item => item.id !== key);
      lsWrite(STORAGE_KEYS.SCENARIOS_LIST, filtered);
    } catch (error) {
      console.error('Error removing from scenarios list:', error);
    }
  }
  
  // ============================================================================
  // HISTORY INTEGRATION (NEW)
  // ============================================================================
  
  /**
   * Save scenario history
   * @param {object} historyData - History data from ScenarioHistory
   * @returns {boolean} Success status
   */
  static saveHistory(historyData) {
    try {
      // F1-FIX: compress history (may contain multiple full scenario snapshots)
      const ok = lsWrite(STORAGE_KEYS.SCENARIO_HISTORY, historyData);
      if (!ok) console.error('Error saving history: storage write failed (quota?)');
      return ok;
    } catch (error) {
      console.error('Error saving history:', error);
      return false;
    }
  }

  /**
   * Load scenario history
   * @returns {object|null} History data or null
   */
  static loadHistory() {
    try {
      // F1-FIX: lsRead handles compressed (z: prefix) and legacy plain JSON
      return lsRead(STORAGE_KEYS.SCENARIO_HISTORY);
    } catch (error) {
      console.error('Error loading history:', error);
      return null;
    }
  }
  
  /**
   * Clear scenario history
   * @returns {boolean} Success status
   */
  static clearHistory() {
    try {
      localStorage.removeItem(STORAGE_KEYS.SCENARIO_HISTORY);
      return true;
    } catch (error) {
      console.error('Error clearing history:', error);
      return false;
    }
  }
  
  // ============================================================================
  // MIGRATION
  // ============================================================================
  
  /**
   * Migrate v1.0 scenario to v2.0 format
   * @private
   */
  static migrateV1ToV2(v1Scenario) {
    
    return {
      version: '2.0',
      scenario_type: 'HIERARCHICAL',
      scenario_name: v1Scenario.scenario_name || 'Migrated Scenario',
      mode: 'STANDARD',
      created_at: v1Scenario.created_at || new Date().toISOString(),
      modified_at: new Date().toISOString(),
      author: 'Dr. Tatar Radu MD',
      
      force: {
        echelon: v1Scenario.force_type || 'BRIGADE',
        total_strength: v1Scenario.initial_strength || 4000,
        national_standard: 'NATO',
        
        ...(v1Scenario.force_type === 'BRIGADE' && {
          brigade: {
            brigade_name: v1Scenario.scenario_name || 'Brigade',
            strength: v1Scenario.initial_strength || 4000,
            unit_type: 'MECHANIZED'
          }
        })
      },
      
      operation_parameters: v1Scenario.operation_type ? {
        configured: true,
        operation_type: v1Scenario.operation_type,
        operational_posture: v1Scenario.operational_posture,
        duration_days: v1Scenario.duration_days,
        environmental_factors: v1Scenario.environmental_factors
      } : null,
      
      medical_configuration: v1Scenario.mtf_distances ? {
        configured: true,
        mtf_distances: v1Scenario.mtf_distances,
        evacuation_assets: v1Scenario.evacuation_assets
      } : null,
      
      calculation_results: v1Scenario.calculation_results ? {
        completed: true,
        ...v1Scenario.calculation_results
      } : null,
      
      reports_generated: v1Scenario.reports_generated || false,
      
      _migrated_from_v1: true,
      _original_v1_data: v1Scenario
    };
  }
  
  // ============================================================================
  // UTILITIES
  // ============================================================================
  
  /**
   * Clear all PSP/CRE data from localStorage
   * @param {boolean} includeV1 - Whether to also clear v1.0 data
   */
  static clearAll(includeV1 = false) {
    try {
      localStorage.removeItem(STORAGE_KEYS.CURRENT_SCENARIO);
      localStorage.removeItem(STORAGE_KEYS.SCENARIOS_LIST);
      localStorage.removeItem(STORAGE_KEYS.SCENARIO_HISTORY);

      const list = this.getScenariosList();
      list.forEach(item => {
        localStorage.removeItem(`psp_cre_scenario_${item.id}`);
      });

      if (includeV1) {
        localStorage.removeItem(STORAGE_KEYS.V1_SCENARIOS);
      }

      // Clear calculation state (baseline, dirty flag, variants)
      localStorage.removeItem('psp_calculation_baseline');
      localStorage.removeItem('psp_calculation_dirty');
      try {
        // F1-FIX: index written with lsWrite — must read with lsRead
        const variantIndex = lsRead('psp_calculation_variant_index') || [];
        variantIndex.forEach(id => localStorage.removeItem(`psp_calculation_variant_${id}`));
      } catch (_) {}
      localStorage.removeItem('psp_calculation_variant_index');

      // Clear Tab6 exercise state (baseline, events, variants)
      localStorage.removeItem('psp_exercise_baseline');
      localStorage.removeItem('psp_exercise_events');
      localStorage.removeItem('psp_exercise_comparison');
      try {
        // F1-FIX: index written with lsWrite — must read with lsRead
        const exerciseIndex = lsRead('psp_exercise_variant_index') || [];
        exerciseIndex.forEach(id => localStorage.removeItem(`psp_exercise_variant_${id}`));
      } catch (_) {}
      localStorage.removeItem('psp_exercise_variant_index');

      // Clear logistics projection cache
      localStorage.removeItem('psp_logistics_projection');

      return true;
    } catch (error) {
      console.error('Error clearing localStorage:', error);
      return false;
    }
  }
  
  /**
   * Get storage usage statistics (NEW)
   * @returns {object} Storage statistics
   */
  static getStorageStats() {
    try {
      let totalSize = 0;
      const stats = {
        total_keys: 0,
        scenario_count: 0,
        current_scenario_size: 0,
        history_size: 0,
        total_size_bytes: 0,
        total_size_kb: 0
      };
      
      const keys = Object.keys(localStorage);
      
      keys.forEach(key => {
        if (key.startsWith('psp_cre_')) {
          stats.total_keys++;
          const value = localStorage.getItem(key);
          const size = new Blob([value]).size;
          totalSize += size;
          
          if (key === STORAGE_KEYS.CURRENT_SCENARIO) {
            stats.current_scenario_size = size;
          } else if (key === STORAGE_KEYS.SCENARIO_HISTORY) {
            stats.history_size = size;
          } else if (key.startsWith('psp_cre_scenario_') && key !== STORAGE_KEYS.CURRENT_SCENARIO) {
            stats.scenario_count++;
          }
        }
      });
      
      stats.total_size_bytes = totalSize;
      stats.total_size_kb = (totalSize / 1024).toFixed(2);
      
      return stats;
    } catch (error) {
      console.error('Error getting storage stats:', error);
      return null;
    }
  }
  
  // ============================================================================
  // EXPORT/IMPORT
  // ============================================================================
  
  /**
   * Export scenario as JSON file
   */
  static exportScenario(scenario) {
    try {
      const dataStr = JSON.stringify(scenario, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${scenario.scenario_name || 'scenario'}_${new Date().getTime()}.json`;
      link.click();
      
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error('Error exporting scenario:', error);
      return false;
    }
  }
  
  /**
   * Export ALL data (scenarios + preferences + history) (NEW)
   * @returns {object} All PSP/CRE data
   */
  static exportAllData() {
    try {
      const data = {
        exported_at: new Date().toISOString(),
        version: '2.0',
        current_scenario: this.getCurrentScenario(),
        scenarios_list: this.getScenariosList(),
        user_preferences: this.loadPreferences(),
        history: this.loadHistory(),
        scenarios: []
      };
      
      // Get all saved scenarios
      const scenariosList = this.getScenariosList();
      scenariosList.forEach(meta => {
        const scenario = this.loadScenario(meta.id);
        if (scenario) {
          data.scenarios.push(scenario);
        }
      });
      
      return data;
    } catch (error) {
      console.error('Error exporting all data:', error);
      return null;
    }
  }
  
  /**
   * Import scenario from JSON file
   */
  static async importScenario(file) {
    try {
      const text = await file.text();
      const scenario = JSON.parse(text);
      
      if (!scenario.version) {
        throw new Error('Invalid scenario file: missing version');
      }
      
      if (scenario.version === '1.0') {
        return this.migrateV1ToV2(scenario);
      }
      
      return scenario;
    } catch (error) {
      console.error('Error importing scenario:', error);
      return null;
    }
  }
  
  /**
   * Import all data from exported JSON (NEW)
   * @param {object} data - Exported data object
   * @returns {boolean} Success status
   */
  static importAllData(data) {
    try {
      // Save current scenario
      if (data.current_scenario) {
        this.saveCurrentScenario(data.current_scenario);
      }
      
      // Save user preferences
      if (data.user_preferences) {
        this.savePreferences(data.user_preferences);
      }
      
      // Save history
      if (data.history) {
        this.saveHistory(data.history);
      }
      
      // Save all scenarios
      if (data.scenarios && Array.isArray(data.scenarios)) {
        data.scenarios.forEach((scenario, index) => {
          const scenarioId = scenario.id || `imported_${index}_${Date.now()}`;
          this.saveScenario(scenarioId, scenario);
        });
      }
      
      return true;
    } catch (error) {
      console.error('Error importing all data:', error);
      return false;
    }
  }
  
  // ============================================================================
  // PREFERENCES
  // ============================================================================
  
  /**
   * Save user preferences
   */
  static savePreferences(preferences) {
    try {
      // F1-FIX: compress for consistency (small payload, negligible savings but uniform API)
      lsWrite(STORAGE_KEYS.USER_PREFERENCES, preferences);
      return true;
    } catch (error) {
      console.error('Error saving preferences:', error);
      return false;
    }
  }

  /**
   * Load user preferences
   */
  static loadPreferences() {
    try {
      // F1-FIX: lsRead handles compressed and legacy plain JSON
      const prefs = lsRead(STORAGE_KEYS.USER_PREFERENCES);
      return prefs || this.getDefaultPreferences();
    } catch (error) {
      console.error('Error loading preferences:', error);
      return this.getDefaultPreferences();
    }
  }
  
  /**
   * Get default user preferences (NEW)
   * @returns {object} Default preferences
   */
  static getDefaultPreferences() {
    return {
      default_national_standard: 'ROMANIAN',
      default_force_echelon: 'BATTALION',
      show_advanced_options: false,
      auto_save: true,
      auto_save_interval_minutes: 5,
      theme: 'light'
    };
  }
}

export default LocalStorageManager;
